import { useEffect, useId, useRef, useState } from "react";
import { api } from "../../api/apiClient";
import type {
  Location,
  OneTimeExpenseCategory,
  OneTimeExpenseRecord,
} from "../../api/types";
import { businessToday } from "../../utils/businessDay";
import { parseTypedAmount } from "../../utils/typedAmount";
import { Chip } from "../ui/Chip";
import { Select } from "../ui/Select";
import { AmountReadBack } from "./AmountReadBack";
import { ONE_TIME_CATEGORIES, ONE_TIME_CATEGORY_LABEL } from "./expenseCategories";

/**
 * G-135 (OD-175): the one-off expense form as a bottom sheet (the house
 * `.inv-sheet` shell). The receipt photo comes first and is REQUIRED for a new
 * expense; the invoice PDF and the note are optional rows. Edit mode (owner)
 * uses the same sheet, with the receipt shown as a thumbnail — it is not
 * replaceable, as before — and no invoice row (the list's "Add invoice" owns
 * that, S4).
 */

export function ExpenseSheet({
  existing,
  locations,
  isOwner,
  fallbackLocation,
  onClose,
  onSaved,
}: {
  existing: OneTimeExpenseRecord | null;
  locations: Location[];
  isOwner: boolean;
  fallbackLocation: { id: string; name: string; code: string } | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [locationId, setLocationId] = useState(
    existing?.locationId ??
      (isOwner ? (locations[0]?.id ?? "") : (fallbackLocation?.id ?? "")),
  );
  const todayYmd = useRef(businessToday().ymd).current;
  const [incurredOn, setIncurredOn] = useState(existing?.incurredOn ?? todayYmd);
  const [amount, setAmount] = useState(existing?.amountEur ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [notesOpen, setNotesOpen] = useState(Boolean(existing?.notes));
  const [category, setCategory] = useState<OneTimeExpenseCategory>(
    existing?.category ?? "REPAIR",
  );
  const [receipt, setReceipt] = useState<File | null>(null);
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptUnreadable, setReceiptUnreadable] = useState(false);
  const [invoice, setInvoice] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const ids = useId();

  useEffect(() => {
    if (!locationId && isOwner && locations.length > 0) {
      setLocationId(locations[0].id);
    }
  }, [locationId, isOwner, locations]);

  // The thumbnail: the photo just taken, or (editing) the one on file.
  useEffect(() => {
    if (!receipt) return;
    const url = URL.createObjectURL(receipt);
    setReceiptUrl(url);
    setReceiptUnreadable(false);
    return () => URL.revokeObjectURL(url);
  }, [receipt]);

  const existingId = existing?.id;
  const existingHasReceipt = existing?.hasReceipt ?? false;
  useEffect(() => {
    if (!existingId || !existingHasReceipt) return;
    let url: string | null = null;
    let cancelled = false;
    void api.fetchOneTimeExpenseReceiptBlobUrl(existingId).then((u) => {
      if (cancelled) {
        if (u) URL.revokeObjectURL(u);
        return;
      }
      url = u;
      setReceiptUrl(u);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [existingId, existingHasReceipt]);

  // No way out mid-save: closing then would let a second tap file the expense twice.
  const close = () => {
    if (!saving) onClose();
  };
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const shopName = isOwner
    ? (locations.find((l) => l.id === locationId)?.name ?? existing?.location.name ?? "")
    : (fallbackLocation?.name ?? existing?.location.name ?? "");
  const title = `${existing ? "Edit expense" : "New expense"}${shopName ? ` · ${shopName}` : ""}`;

  const submit = async () => {
    setLocalError(null);
    if (!existing && !receipt) {
      setLocalError("Add a photo of the receipt first.");
      return;
    }
    if (!locationId) {
      setLocalError("Choose which shop paid for this.");
      return;
    }
    const read = parseTypedAmount(amount);
    if (!read.ok || read.value <= 0) {
      setLocalError(read.ok ? "Enter how much it cost, e.g. 800." : read.message);
      return;
    }
    if (description.trim().length < 2) {
      setLocalError("Say what it was, e.g. New fridge.");
      return;
    }
    if (!incurredOn) {
      setLocalError("Choose the date the money was spent.");
      return;
    }
    setSaving(true);
    try {
      if (existing) {
        await api.updateOneTimeExpense(existing.id, {
          locationId,
          incurredOn,
          amountEur: read.value,
          description: description.trim(),
          notes: notes.trim() || null,
          category,
        });
      } else if (receipt) {
        await api.createOneTimeExpense(
          {
            locationId,
            incurredOn,
            amountEur: read.value,
            description: description.trim(),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
            category,
          },
          receipt,
          invoice ?? undefined,
        );
      }
      await onSaved();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : "Could not save this expense");
    } finally {
      setSaving(false);
    }
  };

  const receiptInputId = `${ids}-receipt`;
  const invoiceInputId = `${ids}-invoice`;

  return (
    <div
      className="inv-sheet-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="inv-sheet exp-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="exp-sheet-head">
          <div className="inv-sheet-title">{title}</div>
          <button type="button" className="inv-sheet-close" onClick={close} disabled={saving}>
            Close
          </button>
        </div>

        {localError ? <div className="alert error">{localError}</div> : null}

        {existing ? (
          receiptUrl ? (
            <img className="exp-receipt-preview" src={receiptUrl} alt="Receipt" />
          ) : null
        ) : (
          <div className="exp-receipt">
            <input
              id={receiptInputId}
              className="exp-file-input"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                // A cancelled Retake fires change with no file: keep the photo already taken.
                const file = e.target.files?.[0];
                if (!file) return;
                setReceipt(file);
                setLocalError(null);
              }}
            />
            {receipt && receiptUrl && !receiptUnreadable ? (
              <div className="exp-receipt-done">
                <img
                  className="exp-receipt-thumb"
                  src={receiptUrl}
                  alt="The receipt photo you took"
                  onError={() => setReceiptUnreadable(true)}
                />
                <label htmlFor={receiptInputId} className="button tinted exp-retake">
                  Retake
                </label>
              </div>
            ) : receipt ? (
              <div className="exp-receipt-done">
                <span className="exp-file-name">{receipt.name}</span>
                <label htmlFor={receiptInputId} className="button tinted exp-retake">
                  Retake
                </label>
              </div>
            ) : (
              <label htmlFor={receiptInputId} className="exp-receipt-tile">
                <span className="exp-receipt-title">Photo of the receipt</span>
                <span className="exp-receipt-required">Required</span>
              </label>
            )}
          </div>
        )}

        {isOwner ? (
          <div className="field">
            <label htmlFor={`${ids}-shop`}>Which shop paid</label>
            <Select
              id={`${ids}-shop`}
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </Select>
          </div>
        ) : null}

        <div className="field">
          <label htmlFor={`${ids}-amount`}>Amount</label>
          <div className="exp-amount">
            <span className="exp-amount-prefix" aria-hidden="true">
              €
            </span>
            <input
              id={`${ids}-amount`}
              className="exp-amount-input"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <AmountReadBack raw={amount} />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-desc`}>What was it</label>
          <input
            id={`${ids}-desc`}
            type="text"
            maxLength={200}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="New fridge"
          />
        </div>

        <div className="field">
          <label htmlFor={`${ids}-date`}>
            Date{incurredOn === todayYmd ? " · Today" : ""}
          </label>
          <input
            id={`${ids}-date`}
            type="date"
            value={incurredOn}
            onChange={(e) => setIncurredOn(e.target.value)}
          />
          <p className="field-hint">Use the date on the receipt; it counts in that month.</p>
        </div>

        <div className="field">
          <span className="opcost-chip-label">Type of expense</span>
          <div className="opcost-chip-row" role="group" aria-label="Type of expense">
            {ONE_TIME_CATEGORIES.map((value) => (
              <Chip
                key={value}
                label={ONE_TIME_CATEGORY_LABEL[value]}
                selected={category === value}
                onClick={() => setCategory(value)}
              />
            ))}
          </div>
        </div>

        {existing ? null : (
          <div className="exp-row">
            <input
              id={invoiceInputId}
              className="exp-file-input"
              type="file"
              accept="application/pdf"
              onChange={(e) => setInvoice(e.target.files?.[0] ?? null)}
            />
            <span className="exp-row-label">
              Invoice PDF <span className="muted">· Optional</span>
              {invoice ? <span className="exp-file-name">{invoice.name}</span> : null}
            </span>
            {invoice ? (
              <button
                type="button"
                className="button plain"
                onClick={() => {
                  setInvoice(null);
                  const input = document.getElementById(invoiceInputId) as HTMLInputElement | null;
                  if (input) input.value = "";
                }}
              >
                Remove
              </button>
            ) : (
              <label htmlFor={invoiceInputId} className="button tinted">
                Attach
              </label>
            )}
          </div>
        )}

        {notesOpen ? (
          <div className="field">
            <label htmlFor={`${ids}-notes`}>Note · Optional</label>
            <textarea
              id={`${ids}-notes`}
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Who did the work, warranty, what happened"
            />
          </div>
        ) : (
          <div className="exp-row">
            <span className="exp-row-label">
              Note <span className="muted">· Optional</span>
            </span>
            <button type="button" className="button tinted" onClick={() => setNotesOpen(true)}>
              Add
            </button>
          </div>
        )}

        <div className="exp-sheet-footer">
          <button
            type="button"
            className="button exp-save"
            onClick={() => void submit()}
            disabled={saving}
          >
            {saving ? "Saving…" : existing ? "Save changes" : "Save expense"}
          </button>
        </div>
      </div>
    </div>
  );
}
